/**
 * useCadenceTodayQueue.ts
 * W7-CadenceTodoFeed — Mi6 Attilius Intelligence Platform
 *
 * Hook that fetches the BDR cadence queue for today.
 * Polls /api/cadence/queue/today every 60 seconds.
 * Uses the same useEffect/useState + api() pattern as live-data.tsx.
 *
 * Canon: no exclamation points, no emojis, M-PACT hyphenated.
 */

import { useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/session";

export interface CadenceQueueItem {
  enrollmentId: string;
  prospectName: string;
  company: string;
  title: string;
  currentStepNumber: number;
  totalSteps: number;
  actionKind: string;
  actionTitle: string;
  actionScript: string | null;
  dueAt: string | null;
  daysOverdue: number;
  lastActionAt: string | null;
  marketingHookKind: string | null;
}

export type CompleteStepOutcome =
  | "connected"
  | "no_answer"
  | "bounced"
  | "booked_meeting"
  | "other";

export interface CompleteStepArgs {
  enrollmentId: string;
  outcome: CompleteStepOutcome | null;
  notes?: string;
}

export interface UseCadenceTodayQueueResult {
  queue: CadenceQueueItem[];
  loading: boolean;
  error: string | null;
  markStepDone: (args: CompleteStepArgs) => Promise<void>;
  completing: string | null;
  refetch: () => void;
}

export function useCadenceTodayQueue(): UseCadenceTodayQueueResult {
  const [queue, setQueue] = useState<CadenceQueueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [completing, setCompleting] = useState<string | null>(null);
  const cancelled = useRef(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    cancelled.current = false;
    setLoading(true);
    setError(null);

    api<{ queue: CadenceQueueItem[] }>("/api/cadence/queue/today")
      .then((res) => {
        if (cancelled.current) return;
        setQueue(Array.isArray(res?.queue) ? res.queue : []);
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled.current) return;
        setError(e?.message ?? "Could not load cadence queue");
        setLoading(false);
      });

    return () => { cancelled.current = true; };
  }, [tick]);

  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  const refetch = useCallback(() => setTick((n) => n + 1), []);

  const markStepDone = useCallback(async (args: CompleteStepArgs): Promise<void> => {
    setCompleting(args.enrollmentId);
    const previousQueue = queue;
    setQueue((prev) => prev.filter((item) => item.enrollmentId !== args.enrollmentId));

    try {
      await api("/api/cadence/complete-step", {
        method: "POST",
        body: JSON.stringify({ enrollmentId: args.enrollmentId, outcome: args.outcome, notes: args.notes ?? null }),
      });
      setTick((n) => n + 1);
    } catch (e: any) {
      setQueue(previousQueue);
      setError(e?.message ?? "Could not complete step");
    } finally {
      setCompleting(null);
    }
  }, [queue]);

  return { queue, loading, error, markStepDone, completing, refetch };
}
